import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { monospaceStyle } from "../../theme";
import {
  BearerGroup,
  CapabilityPartBearers,
} from "../../types/capabilityPartDetail";
import {
  CapabilityBreakdown,
  EntityRef,
  FactSource,
  Page,
  Stance,
} from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import SourceMarks from "../Detail/SourceMarks";
import {
  Beneath,
  ClaimPartName,
  NONE,
  StanceChip,
  columnHeader,
  quiet,
} from "../Detail/claimCells";
import { codeWords } from "../Detail/format";

export interface PartBearersProps {
  // The id of the part the page is about: in a row it is set apart and is
  // not a link to itself
  partId: string;
  bearers: CapabilityPartBearers;
  // True while the rows shown are those of the page asked for before
  turning?: boolean;
  // Asks for the page of one group that starts after `offset` bearers
  onPage: (group: BearerGroup, offset: number) => void;
}

// One row, whatever the type of its bearer
interface BearerRow {
  bearer: EntityRef;
  // The curated class of a product bearer; null for another bearer and for
  // a product under no class
  category: EntityRef | null;
  // The combined capability the bearer claims, as its parts by role; there
  // is no view of the combined capability itself
  capability: CapabilityBreakdown;
  stance: Stance;
  summary: string | null;
  basis: string | null;
  maturity: string | null;
  sources: FactSource[];
}

// The groups in the order of the tabs, with the name of each
const GROUPS: { group: BearerGroup; label: string; noun: string }[] = [
  { group: "products", label: "Products", noun: "products" },
  { group: "organizations", label: "Organizations", noun: "organizations" },
  { group: "projects", label: "Projects", noun: "projects" },
  { group: "categories", label: "Categories", noun: "categories" },
];

// The basis of a claim a document states as such; any other is named
const STATED = "stated";

// The columns of the claim table, with the bearer after the stance: the
// claim is split into its parts, so that it can be compared down a column
const COLUMNS = [
  "Stance",
  "Bearer",
  "Ability",
  "Condition",
  "Mode",
  "Means",
  "Maturity",
  "Src",
];

// The order the rows of every group run in, as the pager line names it
const ROW_ORDER = "has, then enables, requires, lacks";

// The width (px) below which the table scrolls sideways
const TABLE_MIN_WIDTH = 880;

// The total of every group: the bearers of the part
export const countBearers = (bearers: CapabilityPartBearers): number =>
  GROUPS.reduce((count, { group }) => count + bearers[group].total, 0);

// The page of one group as rows; a product is named by its card, with the
// class it is filed under
const rowsOf = (
  bearers: CapabilityPartBearers,
  group: BearerGroup,
): Page<BearerRow> => {
  if (group === "products") {
    const page = bearers.products;
    return {
      ...page,
      items: page.items.map(({ product, ...claim }) => ({
        ...claim,
        bearer: { id: product.id, type: product.type, name: product.name },
        category: product.category,
      })),
    };
  }
  const page = bearers[group];
  return {
    ...page,
    items: page.items.map((claim) => ({ ...claim, category: null })),
  };
};

const tabId = (group: BearerGroup): string => `part-bearers-tab-${group}`;
const panelId = (group: BearerGroup): string => `part-bearers-panel-${group}`;

// An object beneath its ability: a smaller link in the quieter colour
const quieterLink = {
  ...quiet,
  fontSize: "0.75rem",
  textDecorationColor: "currentColor",
} as const;

// The parts of one role, one beneath the other. The page's own part is set
// apart, keeps its mark and is not a link to itself; every other part is a
// link to its view, curated or not, without the mark of the article's own
// words.
const RowParts: React.FC<{
  parts: CapabilityBreakdown["conditions"];
  partId: string;
  quieter?: boolean;
}> = ({ parts, partId, quieter = false }) => (
  <>
    {parts.map((part) => (
      <Box key={part.part.id}>
        {part.part.id === partId ? (
          <ClaimPartName part={part} quieter={quieter} own />
        ) : (
          <EntityLink
            id={part.part.id}
            type={part.part.type}
            name={part.part.name}
            sx={quieter ? quieterLink : undefined}
          />
        )}
      </Box>
    ))}
  </>
);

// The parts of one role in their column; a dash for none
const PartsCell: React.FC<{
  parts: CapabilityBreakdown["conditions"];
  partId: string;
}> = ({ parts, partId }) => (
  <TableCell>
    {parts.length > 0 ? <RowParts parts={parts} partId={partId} /> : NONE}
  </TableCell>
);

const Row: React.FC<{ row: BearerRow; partId: string }> = ({ row, partId }) => {
  const { capability } = row;

  return (
    <TableRow sx={{ verticalAlign: "top" }}>
      <TableCell>
        <StanceChip stance={row.stance} />
      </TableCell>
      <TableCell>
        <EntityLink
          id={row.bearer.id}
          type={row.bearer.type}
          name={row.bearer.name}
        />
        {row.category && (
          <Box
            component="span"
            sx={{ color: "text.disabled", fontSize: "0.75rem" }}
          >
            {` · ${row.category.name ?? ""}`}
          </Box>
        )}
      </TableCell>
      <TableCell>
        {capability.abilities.length === 0 &&
          capability.objects.length === 0 &&
          NONE}
        <RowParts parts={capability.abilities} partId={partId} />
        <RowParts parts={capability.objects} partId={partId} quieter />
        {row.summary && <Beneath>{row.summary}</Beneath>}
        {row.basis && row.basis !== STATED && (
          <Typography
            variant="caption"
            component="div"
            title="No source states the claim as such"
            sx={{ color: "text.disabled", fontStyle: "italic" }}
          >
            {codeWords(row.basis)}
          </Typography>
        )}
      </TableCell>
      <PartsCell parts={capability.conditions} partId={partId} />
      <PartsCell parts={capability.modes} partId={partId} />
      <PartsCell parts={capability.means} partId={partId} />
      <TableCell>{row.maturity ? codeWords(row.maturity) : NONE}</TableCell>
      <TableCell>
        <SourceMarks sources={row.sources} />
      </TableCell>
    </TableRow>
  );
};

// Which bearers of the whole list the page holds and the order they run in,
// and, for a list that does not fit on one page, the steps to the pages
// before and after
const Pager: React.FC<{
  page: Page<BearerRow>;
  noun: string;
  onPage: (offset: number) => void;
}> = ({ page, noun, onPage }) => {
  if (page.items.length === 0) {
    return null;
  }
  const first = page.offset + 1;
  const last = page.offset + page.items.length;
  const paged = page.total > page.items.length;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 0.5,
        mt: 0.5,
        minHeight: 34,
      }}
    >
      <Typography variant="caption" sx={quiet}>
        <Box component="span" sx={monospaceStyle}>
          {`${first}–${last} of ${page.total}`}
        </Box>
        {` · ${ROW_ORDER}`}
      </Typography>
      {paged && (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label={`Previous page of ${noun}`}
            disabled={page.offset === 0}
            onClick={() => onPage(Math.max(0, page.offset - page.limit))}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Next page of ${noun}`}
            disabled={last >= page.total}
            onClick={() => onPage(page.offset + page.limit)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

/**
 * Who is said to have, enable, require or lack a part: one tab per type of
 * bearer that has any, with its count. A row shows the stance, the bearer
 * (a product with its class after it), the claim split into its parts
 * (ability with its object beneath, condition, mode, means), the maturity
 * and the sources. The part the page is about is set apart in its column
 * and is not a link; every other part leads to its view.
 *
 * Beneath the table a line says which rows of the group the tab shows and
 * in what order. Each tab is paged by itself: its pager asks for that
 * group's next page and the other groups stay where they are.
 */
const PartBearers: React.FC<PartBearersProps> = ({
  partId,
  bearers,
  turning = false,
  onPage,
}) => {
  const groups = useMemo(
    () => GROUPS.filter(({ group }) => bearers[group].total > 0),
    [bearers],
  );
  const [picked, setPicked] = useState<BearerGroup | null>(null);
  const shown =
    groups.find(({ group }) => group === picked) ?? groups[0] ?? null;

  const page = useMemo(
    () => (shown ? rowsOf(bearers, shown.group) : null),
    [bearers, shown],
  );

  if (!shown || !page) {
    return null;
  }

  return (
    <Box>
      <Tabs
        value={shown.group}
        onChange={(_event, group: BearerGroup) => setPicked(group)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 36, mb: 1 }}
      >
        {groups.map(({ group, label }) => (
          <Tab
            key={group}
            value={group}
            id={tabId(group)}
            aria-controls={panelId(group)}
            sx={{ minHeight: 36, textTransform: "none" }}
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}
              >
                {label}
                <Box
                  component="span"
                  sx={{ ...monospaceStyle, fontSize: "0.75rem", opacity: 0.7 }}
                >
                  {bearers[group].total}
                </Box>
              </Box>
            }
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={panelId(shown.group)}
        aria-labelledby={tabId(shown.group)}
        aria-busy={turning}
      >
        <TableContainer
          sx={{
            overflowX: "auto",
            border: 1,
            borderColor: "divider",
            backgroundColor: "background.raised",
            // The rows of the page before stay, dimmed, while the next loads
            opacity: turning ? 0.6 : 1,
          }}
        >
          <Table size="small" sx={{ minWidth: TABLE_MIN_WIDTH }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column} sx={columnHeader}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {page.items.map((row, index) => (
                <Row
                  key={`${row.bearer.id}-${row.capability.capability.id}-${row.stance}-${index}`}
                  row={row}
                  partId={partId}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pager
          page={page}
          noun={shown.noun}
          onPage={(offset) => onPage(shown.group, offset)}
        />
      </Box>
    </Box>
  );
};

export default PartBearers;
